<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcTemplateSelector',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'optionSelected']);

  const props = defineProps({
    cacheKey: {
      type: String,
      required: false,
    },
    companyId: {
      type: String,
      required: false,
    },
    variant: {
      type: String,
      required: false,
      default: 'light',
    },
    toggleClass: {
      type: String,
      required: false,
    },
    resourceId: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onItemSelected = (...args) => {
    const [item] = args;
    $router.value.push({
      name: 'app.templates.template',
      params: { id: item.id },
    });
  };
  const onItemCreated = (...args) => {
    console.log('onItemCreated', args);
  };

  const messages = ref({
    noTemplateDefined: $fn.value.pgettext(
      'TemplateSelector',
      'No template have been defined yet'
    ),
    selectTemplate: $fn.value.pgettext('TemplateSelector', 'Select template'),
    createTemplate: $fn.value.pgettext(
      'TemplateSelector',
      'Create template...'
    ),
  });
  const items = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    messages: messages.value,
    items: items.value,
    ux: ux.value,
  }));

  const $$load_items = async () => {
    return $fn.value
      .fetch('list_template')
      .then((d) => _.sortBy(d, 'created_at'));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'messages' }, { name: 'items' }, { name: 'ux' }],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: { messages, items, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const selectedItem = computed(() => {
    if (loaded.value) {
      return items.value ? _.find(items.value, { id: props.resourceId }) : null;
    }
    return null;
  });
  const noItemDefined = computed(() => {
    if (loaded.value) {
      return !items.value || items.value.length === 0;
    }
    return null;
  });
  const dropdownToggleClass = computed(() => {
    return 'border ' + (props.toggleClass || '');
  });

  watch(
    () => props.cacheKey,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('cacheKey');
    }
  );
  watch(
    () => props.companyId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('companyId');
    }
  );
  watch(
    () => props.variant,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('variant');
    }
  );
  watch(
    () => props.toggleClass,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('toggleClass');
    }
  );
  watch(
    () => props.resourceId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('resourceId');
    }
  );

  const act_5962862420439144_emit_upxi4hzdt_getActionArgs = async ($event) => {
    return {
      event: 'optionSelected',
      value: {
        option: $event.scope.item,
      },
    };
  };
  const inst_evh_1048678296148443_close_rdm8wdsep_getWhen = async ($event) => {
    return $event.data.modalResult.value;
  };
  const act_1048678296148443_showModal_dijikusni_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateTemplate',
    };
  };

  const act_5962862420439144_emit_upxi4hzdt = new actionTypes.EmitAction({
    actionArgs: act_5962862420439144_emit_upxi4hzdt_getActionArgs,
  });
  const inst_evh_5962862420439144_clickPrevent_likwrpgvs =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_emit_upxi4hzdt,
      event: 'click.prevent',
      displayName: 'emit',
    });
  const inst_evh_5962862420439144_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_clickPrevent_likwrpgvs],
      event: 'click.prevent',
    });
  const act_1048678296148443_reloadComponentData_5k0m9w6rp =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_1048678296148443_close_rdm8wdsep =
    new actionTypes.EventHandlerImpl({
      action: act_1048678296148443_reloadComponentData_5k0m9w6rp,
      event: 'close',
      when: inst_evh_1048678296148443_close_rdm8wdsep_getWhen,
      displayName: 'reloadComponentData',
    });
  const act_1048678296148443_showModal_dijikusni =
    new actionTypes.ShowModalAction({
      actionArgs: act_1048678296148443_showModal_dijikusni_getActionArgs,
      events: [inst_evh_1048678296148443_close_rdm8wdsep],
    });
  const inst_evh_1048678296148443_clickPrevent_c00ii69cq =
    new actionTypes.EventHandlerImpl({
      action: act_1048678296148443_showModal_dijikusni,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_1048678296148443_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1048678296148443_clickPrevent_c00ii69cq],
      event: 'click.prevent',
    });

  const actions = {
    inst_evh_5962862420439144_clickPrevent_likwrpgvs,
    inst_evh_5962862420439144_clickPrevent,
    inst_evh_1048678296148443_close_rdm8wdsep,
    inst_evh_1048678296148443_clickPrevent_c00ii69cq,
    inst_evh_1048678296148443_clickPrevent,
  };

  const evh_5962862420439144_clickPrevent_likwrpgvs = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_clickPrevent_likwrpgvs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1048678296148443_close_rdm8wdsep = async (event, scope) => {
    return await actions.inst_evh_1048678296148443_close_rdm8wdsep.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1048678296148443_clickPrevent_c00ii69cq = async (event, scope) => {
    return await actions.inst_evh_1048678296148443_clickPrevent_c00ii69cq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1048678296148443_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_1048678296148443_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <BDropdown
    :toggle-class="dropdownToggleClass"
    :variant="variant"
    class="apply-inline-button"
    v-if="loaded"
  >
    <template #button-content>
      <ItpText :text="messages.noTemplateDefind" v-if="noItemDefined">
      </ItpText>
      <ItpText
        :text="messages.selectTemplate"
        v-if="!noItemDefined && !selectedItem"
      >
      </ItpText>
      <div v-if="selectedItem">
        <ItpText
          :muted="selectedItem.inactive"
          :strikethrough="selectedItem.inactive"
        >
          <span class="pe-1">
            {{ selectedItem.name }} ({{ selectedItem.template_type }})
          </span>
        </ItpText>
      </div>
    </template>
    <BDropdownItemButton
      :key="item.id"
      v-for="item in items"
      @click.prevent="evh_5962862420439144_clickPrevent($event, { item })"
    >
      <ItpText :muted="item.inactive" :strikethrough="item.inactive">
        <strong> {{ item.name }} ({{ item.template_type }}) </strong>
      </ItpText>
    </BDropdownItemButton>
    <BDropdownDivider v-if="!noItemDefined"> </BDropdownDivider>
    <ItpNavItem
      href="#"
      dropdown-item
      icon="fa-plus"
      :text="messages.createTemplate"
      @click.prevent="evh_1048678296148443_clickPrevent($event, {})"
    >
    </ItpNavItem>
  </BDropdown>
</template>
